import React, { useState } from 'react';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LaptopChromebookOutlinedIcon from '@mui/icons-material/LaptopChromebookOutlined';

import EditModal from '../modals/EditModal';
import TimeSlotField from '../../common/fields/TimeSlotField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import AppSnackbar from '../../common/popups/AppSnackbar';
import useProfileSection from '../../../hooks/useProfileSection';

const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DAY_LABELS = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};

function TimeSlotsSection({ teacher, canEdit, onUpdate }) {
  const { isEditing, openModal, closeModal, save, errors, loading, snackbarProps } =
    useProfileSection(teacher.profileId, onUpdate);

  const [modalData, setModalData] = useState({
    timeSlots: teacher.timeSlots || {
      SUN: [],
      MON: [],
      TUE: [],
      WED: [],
      THU: [],
      FRI: [],
      SAT: [],
    },
  });

  const handleSave = (e) => {
    e.preventDefault();
    save(modalData, { successMessage: 'Time slots updated!' });
  };

  return (
    <div className="sm:p-10 p-0 mb-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="uppercase text-2xl flex items-center">
          <span className="bg-gradient-primary w-2 h-8 mr-3 rounded-full"></span>
          AVAILABLE TIME SLOTS
        </h2>
        {canEdit && (
          <button onClick={openModal} className="text-orange-500 hover:text-orange-400">
            <EditNoteIcon fontSize="large" />
          </button>
        )}
      </div>

      <div className="overflow-y-auto max-h-[600px] pr-2 space-y-8">
        {days
          .filter((day) => (teacher.timeSlots[day] ?? []).length > 0)
          .map((day) => {
            const slots = teacher.timeSlots[day];
            return (
              <div key={day}>
                <div className="flex items-center gap-3 mb-3 sticky top-0 backdrop-blur-md p-2 z-10">
                  <h3 className="text-white font-semibold text-base">{DAY_LABELS[day]}</h3>
                  <hr className="flex-1 border-white/20" />
                  <span className="text-xs text-gray-500">
                    {slots.length} slot{slots.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 px-4">
                  {slots.map((slot, idx) => (
                    <div
                      key={idx}
                      className="relative w-3/4 lg:w-full mx-auto bg-gradient-glass backdrop-blur-sm rounded-xl p-4 flex flex-col sm:flex-row items-center gap-4 border border-white/10 overflow-hidden group hover:border-white/20 transition-all duration-300"
                    >
                      <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-xl" />
                      <div
                        className="relative shrink-0 p-3 rounded-lg border border-white/10"
                        style={{
                          background:
                            'linear-gradient(90deg, rgba(229,64,4,0.15) 0%, rgba(246,145,29,0.15) 100%)',
                        }}
                      >
                        {slot.type?.name?.includes('Home') ? (
                          <HomeWorkOutlinedIcon fontSize="large" className="text-primary" />
                        ) : (
                          <LaptopChromebookOutlinedIcon fontSize="large" className="text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                        <h4 className="text-white font-semibold capitalize truncate font-secondary text-lg sm:text-xl">
                          {slot.type?.name}
                        </h4>
                        <p className="text-gray-400 text-sm mt-0.5">{slot.subject?.name}</p>
                        <p className="text-gray-400 text-sm mt-0.5">
                          {slot.time}
                          {slot.medium?.name && (
                            <>
                              <span className="mx-1.5 opacity-40">·</span>
                              {slot.medium.name}
                            </>
                          )}
                          {slot.city?.name && (
                            <>
                              <span className="mx-1.5 opacity-40">·</span>
                              {slot.city.name}
                            </>
                          )}
                        </p>
                      </div>
                      {/*<div className="shrink-0 px-3 py-1 bg-gradient-primary rounded-full shadow-md shadow-primary/30">*/}
                      {/*  <span className="text-white text-xs font-semibold tracking-wide">*/}
                      {/*    {slot.name}*/}
                      {/*  </span>*/}
                      {/*</div>*/}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        {days.every((day) => (teacher.timeSlots[day] ?? []).length === 0) && (
          <div className="text-center py-10 bg-gradient-glass rounded-lg">
            <p className="text-gray-400 text-lg">No available time slots.</p>
          </div>
        )}
      </div>

      <EditModal isOpen={isEditing} onClose={closeModal} title="Edit Time Slots">
        <form onSubmit={handleSave}>
          <div className="mb-10">
            {errors.timeSlots && <p className="text-red-400 text-sm mb-3">{errors.timeSlots}</p>}
            <TimeSlotField
              timeSlots={modalData.timeSlots}
              setTimeSlots={(ts) => setModalData({ ...modalData, timeSlots: ts })}
              subjects={teacher.subjects}
              medium={teacher.medium}
              cities={teacher.cities}
              classTypes={teacher.classTypes}
              label="Available Time Slots"
              required
            />
          </div>
          <div className="flex justify-end">
            <PrimaryButton className="w-full sm:w-auto" type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </PrimaryButton>
          </div>
        </form>
      </EditModal>

      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default TimeSlotsSection;
